/**
 * Debug logging utility
 *
 * - Build-time control via import.meta.env.PROD
 * - Runtime control via localStorage.DEBUG === 'true'
 */

const isProd = import.meta.env.PROD;

const isDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('DEBUG') === 'true';
};

const isStreamDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('STREAM_DEBUG') === 'true';
};



const isTest = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';

const shouldLogVerbose = (): boolean => {
  // In dev build (non-prod, non-test): logs on by default
  // In prod or test: logs only if DEBUG flag is set
  if (!isProd && !isTest) return true;
  return isDebugEnabled();
};

export const log = {
  debug: (...args: any[]) => {
    if (shouldLogVerbose()) {
      console.debug(...args);
    }
  },

  stream: (...args: any[]) => {
    if (isStreamDebugEnabled()) {
      console.debug(...args);
    }
  },

  info: (...args: any[]) => {
    if (shouldLogVerbose()) {
      console.info(...args);
    }
  },

  warn: (...args: any[]) => {
    if (shouldLogVerbose()) {
      console.warn(...args);
    }
  },

  error: (...args: any[]) => {
    // Always show errors, regardless of env/flag
    console.error(...args);
  },
  
  enable: () => {
    localStorage.setItem('DEBUG', 'true');
    console.log('✅ Debug mode enabled. Refresh the page to see debug logs.');
  },
  
  disable: () => {
    localStorage.removeItem('DEBUG');
    console.log('❌ Debug mode disabled.');
  },
  
  status: () => {
    console.log(`Debug mode: ${isDebugEnabled() ? '✅ ENABLED' : '❌ DISABLED'}`);
    console.log('To enable: debug.enable() or localStorage.setItem("DEBUG", "true")');
    console.log('To disable: debug.disable() or localStorage.removeItem("DEBUG")');
    console.log(`Build env: ${isProd ? 'PRODUCTION' : 'DEVELOPMENT'}`);
  }
};

// Backwards-compatible debug facade used throughout the app
export const debug = {
  log: log.debug,
  stream: log.stream,
  info: log.info,
  warn: log.warn,
  error: log.error,
  enable: log.enable,
  disable: log.disable,
  enableStream: () => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('STREAM_DEBUG', 'true');
    console.log('✅ Stream debug enabled.');
  },

  disableStream: () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('STREAM_DEBUG');
    console.log('❌ Stream debug disabled.');
  },

  streamStatus: () => {
    if (typeof window === 'undefined') return;
    const enabled = localStorage.getItem('STREAM_DEBUG') === 'true';
    console.log(`Stream debug: ${enabled ? '✅ ENABLED' : '❌ DISABLED'}`);
  },
  status: log.status,
};

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).debug = debug;
}

export default debug;
